import moment from "moment";
import type { Cell, SheetData } from "write-excel-file/universal";

export type ExportFormat = 'csv' | 'xlsx';

export interface ExportColumnDef {
    field: string;
    headerName: string;
    /** Display text: the CSV cell, and the Excel cell when there is no typed value. */
    getValue: (row: any) => string;
    /** Number or date for a real (sortable, computable) Excel cell; null falls back to getValue. */
    getTypedValue?: (row: any) => number | Date | null | undefined;
    /** Excel number format of the typed value, e.g. "dd.mm.yyyy" or "0%". */
    excelFormat?: string;
}

export const EXCEL_DATE_FORMAT = "dd.mm.yyyy";
export const EXCEL_DATE_TIME_FORMAT = "dd.mm.yyyy hh:mm";

// Excel refuses cells longer than this and asks to "repair" the file
const EXCEL_MAX_CELL_LENGTH = 32767;
const EXCEL_MIN_COLUMN_WIDTH = 8;
const EXCEL_MAX_COLUMN_WIDTH = 50;

export const escapeCsvValue = (value: string): string => {
    if (/[",\r\n]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
};

export const buildCsv = (columns: ExportColumnDef[], rows: any[]): string => {
    const headerRow = columns.map((c) => escapeCsvValue(c.headerName)).join(",");
    const dataRows = rows.map((row) =>
        columns.map((c) => escapeCsvValue(String(c.getValue(row) ?? ""))).join(",")
    );
    return [headerRow, ...dataRows].join("\n");
};

/**
 * Excel dates carry no time zone and write-excel-file serializes a Date by its UTC
 * time, so the local wall-clock time is passed as UTC to keep the shown date/time.
 */
export const toExcelDate = (m: moment.Moment): Date =>
    new Date(Date.UTC(m.year(), m.month(), m.date(), m.hours(), m.minutes(), m.seconds()));

const toXlsxCell = (column: ExportColumnDef, row: any): { cell: Cell; text: string } => {
    const typed = column.getTypedValue?.(row);
    if (typed instanceof Date && !isNaN(typed.getTime())) {
        const format = column.excelFormat ?? EXCEL_DATE_FORMAT;
        return { cell: { value: typed, type: Date, format }, text: format };
    }
    if (typeof typed === "number" && Number.isFinite(typed)) {
        return {
            cell: { value: typed, type: Number, format: column.excelFormat },
            text: column.getValue(row) ?? "",
        };
    }
    const text = String(column.getValue(row) ?? "").slice(0, EXCEL_MAX_CELL_LENGTH);
    return { cell: text || null, text };
};

/** Sheet rows (bold header first) and column widths in characters, fitted to the content. */
export const buildXlsxSheet = (
    columns: ExportColumnDef[],
    rows: any[],
): { data: SheetData; widths: number[] } => {
    const widths = columns.map((c) => c.headerName.length);
    const dataRows = rows.map((row) =>
        columns.map((column, index) => {
            const { cell, text } = toXlsxCell(column, row);
            widths[index] = Math.max(widths[index], text.length);
            return cell;
        })
    );
    return {
        data: [
            columns.map((c) => ({ value: c.headerName, fontWeight: "bold" as const })),
            ...dataRows,
        ],
        widths: widths.map((w) => Math.min(Math.max(w + 2, EXCEL_MIN_COLUMN_WIDTH), EXCEL_MAX_COLUMN_WIDTH)),
    };
};

// Excel sheet names: at most 31 characters, none of : \ / ? * [ ]
const toSheetName = (name: string) => name.replace(/[:\\/?*[\]]/g, " ").slice(0, 31) || "Export";

export const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

interface ExportRowsParams {
    format: ExportFormat;
    columns: ExportColumnDef[];
    rows: any[];
    /** File name without extension, e.g. "assets-export". */
    fileBaseName: string;
    sheetName: string;
}

/** Writes the rows as CSV or Excel file and hands it to the browser as download. */
export const exportRows = async ({ format, columns, rows, fileBaseName, sheetName }: ExportRowsParams) => {
    if (format === "csv") {
        const blob = new Blob([buildCsv(columns, rows)], { type: "text/csv;charset=utf-8;" });
        downloadBlob(blob, `${fileBaseName}.csv`);
        return;
    }
    // Loaded on demand so the writer stays out of the main bundle
    const { default: writeXlsxFile } = await import("write-excel-file/universal");
    const { data, widths } = buildXlsxSheet(columns, rows);
    const blob = await writeXlsxFile(data, {
        sheet: toSheetName(sheetName),
        columns: widths.map((width) => ({ width })),
        stickyRowsCount: 1,
    }).toBlob();
    downloadBlob(blob, `${fileBaseName}.xlsx`);
};
